import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, ResourceRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    GetStudyPaymentApprovalThresholdsResponse,
    StudyPaymentApprovalThresholdControllerV2Service,
    StudyPaymentApprovalThresholdEntry,
} from '@app/generated-api';
import {
    computedAllDefined,
    computedAllDefinedNotNull,
    effectAllDefinedNotNull,
    toSignalFromObservable,
} from '@app/v2/utils/signal-utils';
import {
    CardComponent,
    CardEditModeContentDirective,
    CardViewModeContentDirective,
} from '@components/card/card.component';
import {
    MdsButtonComponent,
    MdsDatepickerComponent,
    MdsDividerComponent,
    MdsIconButtonComponent,
    MdsTagComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { BaseCardFormComponent } from '../base-card-form/base-card-form.component';
import { MessageDialogComponent, MessageDialogData } from '../dialogs/message-dialog/message-dialog.component';

import { dateFormatPipe } from '@app/pipes/datepipe';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { map } from 'rxjs';
import {
    StudyPaymentApprovalThresholdFormGroup,
    ThresholdRowFormGroup,
} from './study-payment-approval-threshold-card.form';

interface RangeRow {
    levelLabel: string;
    approversLabel: string;
    rangeLabel: string;
}

@Component({
    selector: 'medpace-study-payment-approval-threshold-card',
    imports: [
        NgTemplateOutlet,
        CardComponent,
        CardViewModeContentDirective,
        CardEditModeContentDirective,
        MdsTextFieldComponent,
        MdsDatepickerComponent,
        MdsButtonComponent,
        MdsDividerComponent,
        MdsTagComponent,
        dateFormatPipe,
        UpperCasePipe,
        MdsIconButtonComponent,
    ],
    templateUrl: './study-payment-approval-threshold-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyPaymentApprovalThresholdCardComponent extends BaseCardFormComponent<
    StudyPaymentApprovalThresholdFormGroup,
    GetStudyPaymentApprovalThresholdsResponse
> {
    thresholdResource = input.required<ResourceRef<GetStudyPaymentApprovalThresholdsResponse>>();
    thresholdService = inject(StudyPaymentApprovalThresholdControllerV2Service);
    dialog = inject(MatDialog);
    dateFormatPipe = inject(dateFormatPipe);

    studyId = input.required<number>();

    readonly minEffectiveDate: Date = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(0, 0, 0, 0);
        return d;
    })();

    // view mode only - range visualization of thresholds, based on the current thresholds in the model (or null if no thresholds)
    rangeRows = computedAllDefinedNotNull({ model: this.model }, ({ model }) => {
        const t1 = model.current?.threshold1 ?? null;
        const t2 = model.current?.threshold2 ?? null;
        return this.buildRangeRows(t1, t2);
    });

    // threshold 1 form value mapped to number or null (or undefined if formGroup is null)
    t1 = toSignalFromObservable(this.formGroup, (formGroup) =>
        formGroup.controls.threshold1.valueChangesStartWithCurrentValue().pipe(
            map((valueString) => {
                if (!valueString) return null;
                const n = Number(valueString);
                return isNaN(n) ? null : n;
            })
        )
    );

    // threshold 2 form value mapped to number or null (or undefined if formGroup is null)
    t2 = toSignalFromObservable(this.formGroup, (formGroup) =>
        formGroup.controls.threshold2.valueChangesStartWithCurrentValue().pipe(
            map((valueString) => {
                if (!valueString) return null;
                const n = Number(valueString);
                return isNaN(n) ? null : n;
            })
        )
    );

    // 'live' visualization of the threshold ranges based on the current form values (or null if formGroup is null)
    liveRangeRows = computedAllDefined({ t1: this.t1, t2: this.t2 }, ({ t1, t2 }) => this.buildRangeRows(t1, t2));

    patchFormEffect = effectAllDefinedNotNull(
        { model: this.model, formGroup: this.formGroup },
        ({ model, formGroup }) => {
            formGroup.controls.timeline.clear();
            const rowFormGroups = model.history.map((historyRow) => {
                const rowFormGroup = new ThresholdRowFormGroup(historyRow);
                return rowFormGroup;
            });
            formGroup.controls.timeline.push(rowFormGroups);
            formGroup.timelineFormGroups.set(rowFormGroups);
        }
    );

    config = this.configCardForm({
        onSave: (setModeFn) => {
            const formGroup = this.formGroup();
            if (!formGroup) return;

            const studyId = this.studyId();
            if (!studyId) return;

            const dirtyRows = formGroup.timelineFormGroups().filter((r) => r.isDirty());
            if (dirtyRows.length === 0) {
                setModeFn('view');
                return;
            }

            this.thresholdService
                .studyPaymentApprovalThresholdControllerV2SaveThresholds(studyId, {
                    rows: dirtyRows.map((r) => ({
                        threshold1: r.controls.threshold1.value ?? undefined,
                        threshold2: r.controls.threshold2.value ?? undefined,
                        effectiveDate: r.controls.effectiveDate.value!,
                    })),
                })
                .subscribe({
                    next: (res) => {
                        this.thresholdResource().update((state) => {
                            return { ...state, current: res.current, history: res.history };
                        });
                        setModeFn('view');
                    },
                });
        },
    });

    insert(): void {
        const formGroup = this.formGroup();
        if (!formGroup) return;
        if (!formGroup.valid) return;
        const effectiveDate = formGroup.controls.effectiveDate.value;

        if (!effectiveDate) throw new Error('Effective date is required');

        const t1 = this.t1();
        const t2 = this.t2();

        // get effective date as YYYY-MM-DD string, without day shift from timezone
        const effectiveDate_DateOnlyString = (() => {
            const d = new Date(effectiveDate);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        })();
        const existingIndex = formGroup.controls.timeline.controls.findIndex(
            (r) => r.controls.effectiveDate.value === effectiveDate_DateOnlyString
        );

        if (existingIndex >= 0) {
            const formattedDateString = this.dateFormatPipe
                .transform(effectiveDate_DateOnlyString, undefined, 'local')
                ?.toUpperCase();
            if (!formattedDateString) throw new Error('Failed to format date string');
            this.dialog
                .open<MessageDialogComponent, MessageDialogData, ModalResult>(MessageDialogComponent, {
                    data: {
                        title: 'Overwrite existing entry',
                        bodyText: `A threshold entry for <b>${formattedDateString}</b> already exists. Overwrite it?`,
                        showCancelButton: true,
                        okayButtonLabel: 'Overwrite',
                        cancelButtonLabel: 'Cancel',
                    },
                    ...getModalConfig(ModalSize.Small),
                })
                .afterClosed()
                .subscribe((result) => {
                    if (result === ModalResult.Okay) {
                        this.doInsert(formGroup, t1, t2, effectiveDate_DateOnlyString, existingIndex);
                    }
                });
        } else {
            this.doInsert(formGroup, t1, t2, effectiveDate_DateOnlyString, -1);
        }
    }

    private doInsert(
        formGroup: StudyPaymentApprovalThresholdFormGroup,
        threshold1: number | null | undefined,
        threshold2: number | null | undefined,
        dateStr: string,
        existingIndex: number
    ): void {
        const entry: StudyPaymentApprovalThresholdEntry = {
            threshold1: threshold1 ?? undefined,
            threshold2: threshold2 ?? undefined,
            effectiveDate: dateStr,
            modifiedByFullName: '',
            modifiedTimestamp: '',
        };
        const newRow = new ThresholdRowFormGroup(entry, true);

        const rows = [...formGroup.timelineFormGroups()];
        if (existingIndex >= 0) {
            formGroup.controls.timeline.setControl(existingIndex, newRow);
            rows[existingIndex] = newRow;
        } else {
            const insertAt = formGroup.controls.timeline.controls.findIndex(
                (r) => (r.controls.effectiveDate.value ?? '') < dateStr
            );
            if (insertAt >= 0) {
                formGroup.controls.timeline.insert(insertAt, newRow);
                rows.splice(insertAt, 0, newRow);
            } else {
                formGroup.controls.timeline.push(newRow);
                rows.push(newRow);
            }
        }

        formGroup.timelineFormGroups.set(rows);
        formGroup.controls.threshold1.reset();
        formGroup.controls.threshold2.reset();
        formGroup.controls.effectiveDate.reset(new Date(new Date().setDate(new Date().getDate() + 1)));
    }

    removeRow(row: ThresholdRowFormGroup): void {
        const formGroup = this.formGroup();
        if (!formGroup) return;
        if (!row.isDirty()) return;

        const index = formGroup.controls.timeline.controls.indexOf(row);
        if (index < 0) return;

        // if this dirty row overwrote an existing history entry, restore that entry instead of removing the row
        const effectiveDate = row.controls.effectiveDate.value;
        const originalEntry = this.model()?.history.find((h) => h.effectiveDate === effectiveDate);

        if (originalEntry) {
            const restoredRow = new ThresholdRowFormGroup(originalEntry);
            formGroup.controls.timeline.setControl(index, restoredRow);
            const rows = [...formGroup.timelineFormGroups()];
            rows[index] = restoredRow;
            formGroup.timelineFormGroups.set(rows);
        } else {
            formGroup.controls.timeline.removeAt(index);
            formGroup.timelineFormGroups.set(formGroup.timelineFormGroups().filter((r) => r !== row));
        }
    }

    thresholdSummary(entry: StudyPaymentApprovalThresholdEntry): string {
        if (entry.threshold1 == null && entry.threshold2 == null) return 'No thresholds — all requests Level 1';
        const fmt = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const parts: string[] = [];
        if (entry.threshold1 != null) parts.push(`T1 $${fmt(entry.threshold1)}`);
        if (entry.threshold2 != null) parts.push(`T2 $${fmt(entry.threshold2)}`);
        return parts.join(' · ');
    }

    thresholdSummaryFromRow(row: ThresholdRowFormGroup): string {
        const t1 = row.controls.threshold1.value;
        const t2 = row.controls.threshold2.value;
        if (t1 == null && t2 == null) return 'No thresholds — all requests Level 1';
        const fmt = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const parts: string[] = [];
        if (t1 != null) parts.push(`T1 $${fmt(t1)}`);
        if (t2 != null) parts.push(`T2 $${fmt(t2)}`);
        return parts.join(' · ');
    }

    isCurrentEntry(
        entry: StudyPaymentApprovalThresholdEntry,
        current: StudyPaymentApprovalThresholdEntry | null | undefined
    ): boolean {
        return !!current && entry.effectiveDate === current.effectiveDate;
    }

    // edit mode - a staged row is "active" if it's not a pending edit and its effective date matches the current threshold
    isCurrentRow(row: ThresholdRowFormGroup): boolean {
        const current = this.model()?.current;
        return !!current && row.controls.effectiveDate.value === current.effectiveDate;
    }

    buildRangeRows(t1: number | null, t2: number | null): RangeRow[] {
        if (t1 === null && t2 === null) {
            return [{ levelLabel: 'Level 1', approversLabel: '1 coordinator', rangeLabel: 'All amounts' }];
        }
        if (t1 !== null && t2 === null) {
            return [
                {
                    levelLabel: 'Level 1',
                    approversLabel: '1 coordinator',
                    rangeLabel: `Under $${t1.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
                {
                    levelLabel: 'Level 2',
                    approversLabel: '2 coordinators',
                    rangeLabel: `$${t1.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} and above`,
                },
            ];
        }
        if (t1 === null && t2 !== null) {
            return [
                {
                    levelLabel: 'Level 1',
                    approversLabel: '1 coordinator',
                    rangeLabel: `Under $${t2.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
                {
                    levelLabel: 'Level 3',
                    approversLabel: '2 coord. + 1 mgr',
                    rangeLabel: `$${t2.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} and above`,
                },
            ];
        }
        return [
            {
                levelLabel: 'Level 1',
                approversLabel: '1 coordinator',
                rangeLabel: `Under $${t1!.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
                levelLabel: 'Level 2',
                approversLabel: '2 coordinators',
                rangeLabel: `$${t1!.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - $${t2!.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
                levelLabel: 'Level 3',
                approversLabel: '2 coord. + 1 mgr',
                rangeLabel: `Over $${t2!.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
        ];
    }
}
